import { SaasEligibleLocation } from '@vgrubs/components';

export type SaasEligibleService =
  | 'vDriveSavings'
  | 'virtualRevenue'
  | 'recoveries';

/**
 * What vGrubs brings a location, each with the amount (cents, over the last
 * 30 days) at which it alone qualifies the location for a SaaS fee. The
 * backend applies the same thresholds; they are repeated here only to show
 * why a location is listed.
 */
export const SAAS_ELIGIBLE_SERVICES: {
  key: SaasEligibleService;
  label: string;
  /** For the one-line reason under a row. */
  short: string;
  threshold: number;
  tooltip: string;
}[] = [
  {
    key: 'vDriveSavings',
    label: 'vDrive savings',
    short: 'vDrive',
    threshold: 20000,
    tooltip: 'How much the location saved by delivering with vDrive.',
  },
  {
    key: 'virtualRevenue',
    label: 'Virtual brands revenue',
    short: 'Virtual brands',
    threshold: 30000,
    tooltip: 'Gross sales of the vGrubs virtual brand orders.',
  },
  {
    key: 'recoveries',
    label: 'Recovered orders',
    short: 'Recovered',
    threshold: 10000,
    tooltip: 'Orders with the status recovered.',
  },
];

/**
 * A location also qualifies when the three together are over this (strictly),
 * even with none of them reaching its own threshold.
 */
export const SAAS_ELIGIBLE_TOTAL_THRESHOLD = 20000;

/** The services that reached their own threshold; empty = the combined total. */
export const qualifyingServices = (item: SaasEligibleLocation) =>
  SAAS_ELIGIBLE_SERVICES.filter(({ key, threshold }) => item[key] >= threshold);

/**
 * Why a location is listed, one entry per reason: each service that reached
 * its own threshold ("Recovered orders"), or "All three together" when none
 * did.
 */
export const qualifyingReasons = (item: SaasEligibleLocation) => {
  const services = qualifyingServices(item);
  return services.length > 0
    ? services.map(({ label }) => label)
    : ['All three together'];
};

/**
 * The same as one line: "Qualifies because of Recovered orders", "... of
 * vDrive savings and Recovered orders", "... of all three together".
 */
export const qualifyingLabel = (item: SaasEligibleLocation) => {
  const services = qualifyingServices(item).map(({ label }) => label);
  if (services.length === 0) return 'Qualifies because of all three together';
  const last = services.pop();
  return `Qualifies because of ${
    services.length > 0 ? `${services.join(', ')} and ${last}` : last
  }`;
};

export const openLocationInNewTab = (locationId: number) =>
  window.open(`/locations/${locationId}`, '_blank', 'noopener,noreferrer');
